import React, { useCallback, useMemo } from 'react';
import { type NodeProps, useConnection } from '@xyflow/react';
import {
  NODE_CARD_VARIANT_GATE,
  NODE_CARD_VARIANT_INPUT,
  NODE_CARD_VARIANT_NEUTRAL,
  NODE_CARD_VARIANT_NOTE,
  NODE_CARD_VARIANT_OUTPUT,
  NODE_CARD_VARIANT_TASK,
  type NodeCardVariant,
} from '@aibuild/ui';
import type { NodeDefinitionDto } from '@api/nodeDefinitions';
import type { NodeKind, ProcessArtifactDto } from '@api/processes';
import { useEditorContext } from '../useEditorContext';
import { useNodeDefinitions } from '../../hooks/useNodeDefinitions';
import { resolveIcon } from '../sidebar/iconRegistry';
import { resolveNodeCardState } from './nodeCardState';
import { nodeEmphasisFor } from '../connectionFocus';
import type { Port, ProcessNode as ProcessNodeRfType } from '../graphTypes';

const KIND_VARIANT: Record<NodeKind, NodeCardVariant> = {
  input: NODE_CARD_VARIANT_INPUT,
  task: NODE_CARD_VARIANT_TASK,
  gate: NODE_CARD_VARIANT_GATE,
  output: NODE_CARD_VARIANT_OUTPUT,
  note: NODE_CARD_VARIANT_NOTE,
  image_gen: NODE_CARD_VARIANT_TASK,
  '3d_gen': NODE_CARD_VARIANT_TASK,
  manual: NODE_CARD_VARIANT_GATE,
};

const KIND_FALLBACK_TITLE: Record<NodeKind, string> = {
  input: 'Input',
  task: 'Task',
  gate: 'Gate',
  output: 'Output',
  note: 'Note',
  image_gen: 'Image Generation',
  '3d_gen': '3D Generation',
  manual: 'Manual',
};

const defaultArtifactsStyleFor = (
  kind: NodeKind,
): 'NONE' | 'LIST' | 'PREVIEW' => {
  if (kind === 'note') return 'NONE';
  if (kind === 'output' || kind === 'manual') return 'LIST';
  return 'PREVIEW';
};

export function resolveDefinition(
  catalog: readonly NodeDefinitionDto[] | undefined,
  kind: NodeKind,
  data: ProcessNodeRfType['data'],
): NodeDefinitionDto | undefined {
  if (!catalog) return undefined;
  if (kind === 'task') {
    // A legacy per-skill definition describes a node running that skill alone —
    // its fixed ports would hide the rest of a wider selection.
    const skillIds = (data as { skillIds?: unknown }).skillIds;
    const skillId = Array.isArray(skillIds)
      && skillIds.length === 1
      && typeof skillIds[0] === 'string'
      ? skillIds[0]
      : undefined;
    const bySkill = skillId
      ? catalog.find((def) => def.kind === 'task' && def.id === skillId)
      : undefined;
    return bySkill ?? catalog.find((def) => def.id === 'task');
  }
  if (kind === 'image_gen') return catalog.find((def) => def.id === 'image_gen');
  if (kind === '3d_gen') return catalog.find((def) => def.id === '3d_gen');
  return catalog.find((def) => def.kind === kind);
}

export function useProcessNodeDerivedState({
  id,
  type,
  data,
  selected,
}: NodeProps<ProcessNodeRfType>) {
  const {
    updateNodeData,
    readOnly: published,
    errorsForNode,
    publishHintsForNode,
    runStateForNode,
    isNodeCollapsed,
    toggleNodeCollapsed,
    connectionFocus,
  } = useEditorContext();
  const kind = (type ?? 'task') as NodeKind;
  const errors = errorsForNode(id);
  const publishHints = publishHintsForNode(id);
  const runState = runStateForNode(id);
  // Only `input` / `output` kinds map to the design's source / terminal
  // halos; every other kind falls through to the default resting ring.
  const haloKind: 'input' | 'output' | null =
    kind === 'input' || kind === 'output' ? kind : null;
  // Publish hints tint the ring amber but never gate the run button — a
  // node with only wiring gaps still runs on its own instruction.
  const cardState = resolveNodeCardState({
    selected: Boolean(selected),
    errors,
    publishHints,
    runState,
    nodeKind: haloKind,
  });
  const collapsed = isNodeCollapsed(id);
  const onToggleCollapsed = useCallback(
    () => toggleNodeCollapsed(id),
    [id, toggleNodeCollapsed],
  );

  const dataBag = data as unknown as Record<string, unknown>;
  const definitionsQuery = useNodeDefinitions();
  const definition = useMemo(
    () => resolveDefinition(definitionsQuery.data, kind, data),
    [definitionsQuery.data, kind, data],
  );
  const catalogTitle =
    definition?.displayMetadata.label ?? KIND_FALLBACK_TITLE[kind];
  // {@code data.label} (= {@code process_nodes.label} on the wire) is the
  // user-renameable, persisted node title — written by the 3-dots quick
  // menu's Rename action via {@code useProcessNodeActionHandlers#onRename}.
  // Empty / blank falls back to the catalog default so a freshly-dropped
  // node still reads "Task", "Output", etc. without forcing a rename.
  const rawLabel = dataBag.label;
  const persistedLabel =
    typeof rawLabel === 'string' && rawLabel.trim().length > 0
      ? rawLabel
      : undefined;
  const title = persistedLabel ?? catalogTitle;
  const iconNode = React.createElement(
    resolveIcon(definition?.displayMetadata.iconId),
  );
  const fields = useMemo(() => {
    const allFields = definition?.configFields ?? [];
    if (kind !== 'input') return allFields;
    const dt = dataBag.dataType;
    if (typeof dt === 'string' && dt === 'NONE') {
      return allFields.filter((f) => f.key !== 'required');
    }
    return allFields;
  }, [definition?.configFields, kind, dataBag.dataType]);
  const branches = definition?.branches ?? [];
  const artifactsStyle =
    definition?.artifactsPanel ?? defaultArtifactsStyleFor(kind);
  const isComputable = definition?.isComputable ?? kind !== 'note';
  const variant = KIND_VARIANT[kind] ?? NODE_CARD_VARIANT_NEUTRAL;
  const ports = dataBag.ports as readonly Port[] | undefined;
  const latestArtifacts = dataBag.latestArtifacts as
    | readonly ProcessArtifactDto[]
    | undefined;
  const outputPorts = Array.isArray(ports)
    ? ports.filter((p) => p.direction === 'output')
    : [];
  // A node exposes the add/remove-output editor when its catalog declares a
  // variable output group (Task, Manual). Branched nodes (Gate) keep their
  // fixed named branches instead.
  const hasVariableOutputs =
    (definition?.defaultPorts.outputGroups.length ?? 0) > 0;
  const showOutputRow = hasVariableOutputs && branches.length === 0;
  // Per-output free-text instructions only persist for Task nodes (fed to the
  // agent via config.outputInstructions). Manual keeps a single node-level
  // instruction, so its output editor omits the per-port instruction field.
  const showOutputInstructions = definition?.id === 'task';

  const hasInputPort = useMemo(
    () => Array.isArray(ports) && ports.some((p) => p.direction === 'input'),
    [ports],
  );

  const connection = useConnection();
  const isDropTarget =
    connection.inProgress &&
    hasInputPort &&
    connection.fromNode?.id !== id;
  const emphasis = connection.inProgress
    ? null
    : nodeEmphasisFor(connectionFocus, id);

  return {
    id,
    kind,
    cardState,
    variant,
    collapsed,
    onToggleCollapsed,
    dataBag,
    definition,
    catalogTitle,
    persistedLabel,
    title,
    iconNode,
    fields,
    branches,
    artifactsStyle,
    isComputable,
    ports,
    latestArtifacts,
    outputPorts,
    showOutputRow,
    showOutputInstructions,
    isDropTarget,
    emphasis,
    published,
    updateNodeData,
    runState,
  };
}
